import {first} from '@shared/helpers/array'
import {toNumberString} from '@shared/helpers/format'
import {eq} from '@shared/helpers/lang'

import {
    AllPhonePrefixes,
    PhonePrefixAreaType,
    PhonePrefixEtcType,
    PhonePrefixMobileType,
    VirtualPhoneNumberUnavailableCodeList,
} from '@web/core/constants'

/**
 * 국번을 파싱
 * - 000-1111-2222 -> 000
 * - 대시(-)로 구분된 문자열에서 국번을 파싱합니다
 * - 대시로 파싱할 수 없으면 빈 문자열을 반환합니다
 * */
export const getPhoneNumberPrefix = (value: string): string => {
    return first(value.split('-')) || ''
}

/** 유효한 국번(prefix)인지 검증
 * - AllPhonePrefixes는 언제든 업데이트 될 수 있는 데이터
 * - 예전에 등록된 번호가 AllPhonePrefixes에서 제거된 번호인 경우가 있을수 있으므로, 유효성 검증 함수 필요
 */
export const isValidPhoneNumberPrefix = (value: string): boolean => {
    return AllPhonePrefixes.some((code) => code === value)
}

export const isVirtualPhoneNumberUnavailablePrefix = (value: string): boolean => {
    return VirtualPhoneNumberUnavailableCodeList.some((code) => code === value)
}

/**
 * 안심번호 발급 가능한 번호인지 확인
 * 국번 제외 모두 0이거나 안심번호 발급 가능한 국번인지 확인
 */
export const isVirtualPhoneNumberUnavailable = (value: string): boolean => {
    if (!value) {
        return true
    }

    const [telPart1, telPart2, telPart3] = value.split('-')

    if (!telPart1) {
        return true
    }

    const numberWithoutPrefix = `${telPart2}${telPart3}`

    return isVirtualPhoneNumberUnavailablePrefix(telPart1) || !numberWithoutPrefix.replaceAll('0', '').length
}

/**
 * 입력되는 번호(휴대폰, 지역번호, 기타서비스용 모두 포함)에 대해
 * 자동으로 '-'를 입력해주는 함수입니다
 *
 * 휴대폰 서비스 형식 >> 4자리 - 4자리
 * 지역번호 서비스 형식 >> 3 | 4 자리 - 4자리
 * 기타 서비스용 형식
 * >> 070 은 4자리 - 4자리
 * >> 080 은 3자리 - 4자리
 * >> 그 외는 3 | 4자리 - 4자리
 * */
export function isValidUserPhoneNumber(value: string): boolean {
    /** 4자리 - 4자리 패턴 */
    const regexMobileType = /(^01([0|1|6|7|8|9])|^070)-\d{4}-\d{4}$/
    /** 3|4 자리 - 4자리 패턴 */
    const regexAreaType =
        /(^02|^03([1-3])|^04([1-4])|^05([1-5])|^06([1-4])|^0130|^0303|^050([2-8])?|^012|^059)-\d{3,4}-\d{4}$/
    /** 3자리 - 4자리 패턴 */
    const regexEtcType = /^080-\d{3}-\d{4}$/

    const prefix = value.split('-')[0] ?? ''

    if (PhonePrefixMobileType.includes(prefix)) {
        return regexMobileType.test(value)
    } else if (PhonePrefixAreaType.includes(prefix)) {
        return regexAreaType.test(value)
    } else if (PhonePrefixEtcType.includes(prefix)) {
        return regexEtcType.test(value)
    }
    return false
}

export function isUserPhoneNumberInBlacklist(value: string): boolean {
    /** 블랙리스트 휴대폰번호 */
    const blackList = ['010-0000-0000']

    return blackList.includes(value)
}

/** 연락처 번호에 '-'을 자동으로 추가해주는 함수입니다 */
export function addAutoHyphen(value: string): string {
    return toNumberString(value)
        .replace(
            /(^01[0|1|6|7|8|9]|^02|^03[1-3]|^04[1-4]|^05[1-5]|^06[1-4]|^070|^080|^0130|^0303|^050[2-8]?|^012|^059)(\d+)?(\d{4})$/,
            '$1-$2-$3',
        )
        .replace('--', '-')
}

/** 연락처 번호에 길이별로 '-'을 추가하는 함수 */
export const addHyphenByLength = (value: string | number) => {
    const rawNumber = toNumberString(String(value))
    const numberLengthEqualsTo = eq(rawNumber.length)

    // (0,1,2)-(3,4,5,6)-(7,8,9,10)
    if (numberLengthEqualsTo(11)) {
        return `${rawNumber.substring(0, 3)}-${rawNumber.substring(3, 7)}-${rawNumber.substring(7, 11)}`
    } else if (numberLengthEqualsTo(10)) {
        // (0,1)-(2,3,4,5)-(6,7,8,9)
        if (rawNumber.startsWith('02')) {
            return `${rawNumber.substring(0, 2)}-${rawNumber.substring(2, 6)}-${rawNumber.substring(6, 10)}`
        }
        // (0,1,2)-(3,4,5)-(6,7,8,9)
        return `${rawNumber.substring(0, 3)}-${rawNumber.substring(3, 6)}-${rawNumber.substring(6, 10)}`
    }
    // (0,1,2,3)-(4,5,6,7)
    else if (numberLengthEqualsTo(8)) {
        return `${rawNumber.substring(0, 4)}-${rawNumber.substring(4, 8)}`
    }

    return rawNumber
}

/**
 * 전화번호 문자열에 하이픈(-)을 추가하여 반환하는 함수
 *
 * 11자리: XXX-XXXX-XXXX (일반) 또는 XXXX-XXXX-XXX (013, 050 시작)
 * 10자리: XXX-XXX-XXXX (일반) 또는 XX-XXXX-XXXX (02로 시작)
 * 9자리: XX-XXX-XXXX
 * 8자리: XXXX-XXXX
 */
export const addPhoneNumberHyphen = (value: string | number) => {
    const rawNumber = toNumberString(String(value))

    switch (rawNumber.length) {
        case 12:
            return rawNumber.replace(/^(\d{4})(\d{4})(\d{4})$/, '$1-$2-$3')
        case 11:
            return rawNumber.replace(/^(\d{3})(\d{4})(\d{4})$/, '$1-$2-$3')
        case 10:
            return rawNumber.replace(/^(02)(\d{4})(\d{4})$|^(\d{3})(\d{3})(\d{4})$/, (_, g1, g2, g3, g4, g5, g6) =>
                g1 ? `${g1}-${g2}-${g3}` : `${g4}-${g5}-${g6}`,
            )
        case 9:
            return rawNumber.replace(/^(\d{2})(\d{3})(\d{4})$/, '$1-$2-$3')
        case 8:
            return rawNumber.replace(/^(\d{4})(\d{4})$/, '$1-$2')
        default:
            return rawNumber
    }
}

/**
 * 모든 문자열(정규식 내 '.'에 대응되는 모든 문자)에 대한 정규표현식을 얻는 함수
 * @example
 * getPatternedRegExp({pattern: [[4], [4], [4], [3, 4]]})
 * 4개 문자 / 4개 문자 / 4개 문자 / 3~4개 문자 로 자를 수 있는 형태의 정규식을 반환
 * 포획한 n개 문자로 나뉜 각 그룹은 $1 $2 등으로 접근 가능
 *
 * '1234****5678****'.replace(regExp, '$1 $2 $3 $4') 실행 시
 * '1234 **** 5678 ****' 를 얻을 수 있다.
 * '1234****5678****'.replace(regExp, '$1-$2-$3-$4') 실행 시
 * '1234-****-5678-****' 를 얻을 수 있다.
 *
 * @condition
 * pattern의 각 원소로 들어오는 배열은 각각 minCount, maxCount이므로 2개의 원소까지만 할당할 것
 * minCount, maxCount 순이므로 오름차순으로 넣을 것
 *
 * @example
 * @OK [[4], [4], [3,4], [4,5]]
 * @NOT_OKAY [[4], [3,4,5], [4,5]]
 */
export const getPatternedRegExp = ({pattern}: {pattern: Array<(string | number)[]>}) => {
    const queryPattern = pattern.reduce((acc, digit) => {
        return `${acc}(.{${digit.join(',')}})`
    }, '')

    const regExp = new RegExp(queryPattern)

    return regExp
}

/** null을 포함한 숫자 배열의 reduce를 실행합니다. */
export const reduceWithNullableNumberField = (list: Array<Maybe<number>>) =>
    list.reduce((acc, next) => (acc ?? 0) + (next ?? 0), 0) ?? 0
